import {
  createContext,
  ReactElement,
  useContext,
  useEffect,
  useState,
} from 'react';
import { TraderOrderRankChartDataType } from '../OrderCountRankChart';
import useFetch from '../../../api/useFetch';
import { traderRankGetRequestApi } from '../../../api/apiConfig';
import { formatDashFormToEightDigitString } from '../../../function/replaceString';
import { OrderAmountRankChart } from '../OrderAmountRankChart';
import { ChartContextType } from './chartContextType';

export const OrderCountRankChartProvider = ({
  fetchCount,
  startDt,
  endDt,
  children,
}: {
  fetchCount: number;
  startDt: string;
  endDt: string;
  children: React.ReactNode;
}) => {
  const [data, setData] = useState<TraderOrderRankChartDataType[]>([]);

  const [filteredData, setFilteredData] = useState<
    TraderOrderRankChartDataType[]
  >([]);

  const [pageIndex, setPageIndex] = useState<number>(1); // 현재 페이지

  const itemsPerPage = 10;

  const totalPage = Math.ceil(data.length / itemsPerPage); // 총 페이지 수

  const { fetch: fetchTraderRankGetRequestApi } = useFetch({
    fetchFunction: traderRankGetRequestApi,
    onSuccess: (data: TraderOrderRankChartDataType[]) => {
      const sortedData = [...data].sort(
        (a, b) => b.orderAmount - a.orderAmount
      ); // 새로운 배열로 정렬
      const paginatedData = sortedData.slice(0, itemsPerPage); // 첫 페이지 데이터

      setData(sortedData);
      setFilteredData(paginatedData);
    },
  });

  useEffect(() => {
    fetchCount > 0 &&
      fetchTraderRankGetRequestApi({
        startDt: formatDashFormToEightDigitString(startDt),
        endDt: formatDashFormToEightDigitString(endDt),
      });
  }, [fetchCount]);

  // 페이지 번호에 따라 데이터 필터링
  const setFilterCriteria = (pageIndex: number) => {
    setPageIndex(pageIndex);
    const startIdx = (pageIndex - 1) * itemsPerPage;
    const endIdx = Math.min(pageIndex * itemsPerPage, data.length); // endIdx가 data.length를 넘지 않도록 처리

    setFilteredData(data.slice(startIdx, endIdx)); // 해당 페이지 데이터만 필터링
  };

  return (
    <OrderCountRankChartContext.Provider
      value={{
        data: data,
        filteredData,
        setFilterCriteria,
        totalDataLength: data.length,
        itemsPerPage: itemsPerPage,
        startDt: startDt,
        endDt: endDt,
        pageIndex,
        totalPage,
      }}
    >
      {children}
    </OrderCountRankChartContext.Provider>
  );
};

const OrderCountRankChartContext = createContext<ChartContextType | null>(null);

export const useOrderCountRankChart = () => {
  const context = useContext(OrderCountRankChartContext);
  if (!context) {
    throw new Error(
      'useOrderAmountRankChart must be used within OrderAmountRankChartProvider'
    );
  }
  return context;
};
